import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { roleTemplateFormMessages } from './roleTemplateForm.messages';
import { CreateRoleTemplateRequestSchema } from 'schemas';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface RoleTemplateFormProps {
  onSubmit: (formData: CreateRoleTemplateFormValues) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

type CreateRoleTemplateFormValues = z.infer<
  typeof CreateRoleTemplateRequestSchema
>;

export function RoleTemplateForm({
  onSubmit,
  isSubmitting = false,
  onCancel,
}: RoleTemplateFormProps) {
  const form = useForm<CreateRoleTemplateFormValues>({
    resolver: zodResolver(CreateRoleTemplateRequestSchema),
    defaultValues: {
      title: '',
      description: '',
    },
  });

  const handleFormSubmit = async (data: CreateRoleTemplateFormValues) => {
    await onSubmit(data);
  };

  return (
    <div className="mx-auto w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-6"
          aria-label={roleTemplateFormMessages.formAriaLabel}
        >
          {/* Title Field */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{roleTemplateFormMessages.titleLabel}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={roleTemplateFormMessages.titlePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description Field */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {roleTemplateFormMessages.descriptionLabel}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      roleTemplateFormMessages.descriptionPlaceholder
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Form Actions */}
          <div className="flex gap-4 pt-6">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {roleTemplateFormMessages.cancelButton}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || !form.formState.isValid}
              className="flex-1"
            >
              {isSubmitting
                ? roleTemplateFormMessages.creating
                : roleTemplateFormMessages.createTemplateButton}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
